import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import CTAButtons from "@/components/CTAButtons";
import InquiryDialog from "@/components/InquiryDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  Star,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  Clock3,
  Route,
  Phone,
} from "lucide-react";
import BookingSearch from "@/components/BookingSearch";
import Benefits from "@/components/Benefits";
import ChooseYourVehicle from "@/components/ChooseYourVehicle";

const iconMap = {
  sparkles: Sparkles,
  "shield-check": ShieldCheck,
  "clock-3": Clock3,
  route: Route,
};

function Hero() {
  const { data: slides = [] } = useQuery({
    queryKey: ["hero-slides"],
    queryFn: () =>
      api
        .get("/hero-slides", { params: { is_active: true } })
        .then((r) => r.data.items),
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return;

    const timer = setInterval(() => {
      setCurrentIndex((current) => (current + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    if (!slides.length) {
      setCurrentIndex(0);
      return;
    }

    if (currentIndex >= slides.length) {
      setCurrentIndex(0);
    }
  }, [slides.length, currentIndex]);

  const current = slides[currentIndex];

  const goPrevious = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (current) => (current - 1 + slides.length) % slides.length
    );
  };

  const goNext = () => {
    if (!slides.length) return;

    setCurrentIndex((current) => (current + 1) % slides.length);
  };

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        h-[680px]
        sm:h-[720px]
        lg:h-[780px]
        min-h-0
      "
      data-testid="hero-section"
    >
      {slides.length > 0 ? (
        slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-[1000ms] ease-in-out ${
              index === currentIndex
                ? "opacity-100 z-10"
                : "opacity-0 z-0"
            }`}
            aria-hidden={index !== currentIndex}
          >
            <img
              src={slide.image_url}
              alt={slide.title || "Travel destination"}
              className="
                absolute inset-0
                w-full h-full
                object-cover
                object-[58%_38%]
                sm:object-[50%_42%]
                md:object-center
              "
            />

            <div className="absolute inset-0 bg-black/35" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/15" />
          </div>
        ))
      ) : (
        <div className="absolute inset-0 bg-brand-navy-900" />
      )}

      <div
        className="
          relative z-20
          h-full
          max-w-7xl
          mx-auto
          px-5
          sm:px-8
          lg:px-10
          flex
          items-center
        "
      >
        <div
          className="
            w-full
            max-w-3xl
            pt-16
            sm:pt-10
            lg:pt-0
            pb-24
            sm:pb-20
          "
        >
          <div
            className="
              text-[10px]
              sm:text-xs
              uppercase
              tracking-[0.28em]
              sm:tracking-[0.32em]
              text-amber-400
              mb-4
              sm:mb-5
            "
          >
            Vizag Jagan Travels
          </div>

          <h1
            className="
              font-display
              text-white
              leading-[1.02]
              tracking-tight
              max-w-[680px]
            "
            style={{
              fontSize: "clamp(38px, 10vw, 76px)",
            }}
          >
            {current?.title || "Journey Beyond Boundaries"}
          </h1>

          <div
            className="
              mt-4
              sm:mt-5
              font-display
              italic
              text-amber-300
              text-xl
              sm:text-2xl
              lg:text-3xl
              leading-tight
            "
          >
            Travel with Comfort &amp; Care
          </div>

          <p
            className="
              mt-4
              sm:mt-5
              text-white/90
              text-sm
              sm:text-base
              lg:text-lg
              max-w-xl
              leading-relaxed
            "
          >
            {current?.subtitle ||
              "Safe. Reliable. Memorable. Your trusted travel partner in Vizag and beyond."}
          </p>

          <div
            className="
              mt-6
              sm:mt-8
              flex
              flex-col
              sm:flex-row
              items-start
              gap-3
            "
          >
            <InquiryDialog triggerTestId="hero-book-btn">
              <Button
                className="
                  rounded-full
                  bg-brand-navy-800
                  text-white
                  hover:bg-brand-navy-700
                  px-6
                  h-12
                  text-sm
                  font-semibold
                  shadow-lg
                  w-fit
                "
                data-testid="hero-book-btn"
              >
                Book Your Journey
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </InquiryDialog>

            <Link to="/destinations">
              <Button
                variant="outline"
                className="
                  rounded-full
                  border-white/50
                  bg-white/10
                  backdrop-blur-sm
                  text-white
                  hover:bg-white
                  hover:text-slate-950
                  px-6
                  h-12
                  text-sm
                  font-medium
                  w-fit
                "
                data-testid="hero-packages-btn"
              >
                Explore Destinations
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <div
          className="
            absolute
            z-30
            bottom-7
            sm:bottom-8
            left-1/2
            -translate-x-1/2
            flex
            items-center
            gap-3
          "
        >
          <button
            type="button"
            onClick={goPrevious}
            className="
              w-10
              h-10
              rounded-full
              border
              border-white/40
              bg-black/20
              backdrop-blur-sm
              grid
              place-items-center
              text-white
              hover:bg-white
              hover:text-slate-950
              transition-colors
            "
            aria-label="Previous slide"
            data-testid="hero-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div
            className="
              min-w-[70px]
              text-center
              text-white
              text-xs
              tracking-[0.2em]
              font-medium
            "
          >
            {String(currentIndex + 1).padStart(2, "0")} /{" "}
            {String(slides.length).padStart(2, "0")}
          </div>

          <button
            type="button"
            onClick={goNext}
            className="
              w-10
              h-10
              rounded-full
              border
              border-white/40
              bg-black/20
              backdrop-blur-sm
              grid
              place-items-center
              text-white
              hover:bg-white
              hover:text-slate-950
              transition-colors
            "
            aria-label="Next slide"
            data-testid="hero-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </section>
  );
}

function AboutBlock({ about }) {
  if (!about) return null;

  return (
    <section
      className="
        max-w-7xl
        mx-auto
        px-6
        lg:px-10
        py-28
        grid
        md:grid-cols-12
        gap-12
        items-center
      "
      data-testid="about-block"
    >
      <div className="md:col-span-6">
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
          About the atelier
        </div>

        <h2 className="font-display text-4xl sm:text-5xl text-slate-950 leading-tight">
          {about.title}
        </h2>

        <p className="mt-6 text-slate-600 leading-relaxed text-lg">
          {about.body}
        </p>

        <div className="mt-8">
          <Link to="/about">
            <Button
              variant="outline"
              className="rounded-full"
              data-testid="about-learn-more"
            >
              Read our story →
            </Button>
          </Link>
        </div>
      </div>

      <div className="md:col-span-6 relative">
        <img
          src={about.image_url}
          alt="About"
          className="
            rounded-2xl
            w-full
            aspect-[4/5]
            object-cover
            shadow-2xl
          "
        />

        <div
          className="
            absolute
            -bottom-6
            -left-6
            bg-white
            rounded-xl
            shadow-xl
            border
            border-slate-200
            p-6
            max-w-[240px]
          "
        >
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-1">
            Trusted since
          </div>

          <div className="font-display text-4xl text-slate-950">
            2011
          </div>

          <div className="text-sm text-slate-500 mt-1">
            By 12,400+ discerning travellers.
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChoose({ items = [], stats = [] }) {
  if (!items?.length) return null;

  return (
    <section
      className="
        relative
        bg-brand-navy-900
        text-slate-100
        overflow-hidden
      "
      data-testid="why-choose-section"
    >
      <div className="grain" />

      <div
        className="
          max-w-7xl
          mx-auto
          px-6
          lg:px-10
          py-28
          relative
        "
      >
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-4">
            Why travellers choose us
          </div>

          <h2 className="font-display text-4xl sm:text-5xl leading-tight">
            Small details.
            <br />
            Deeply considered.
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-8">
          {items.map((it, idx) => {
            const Icon = iconMap[it.icon] || Sparkles;

            return (
              <div
                key={idx}
                className="border-t border-white/15 pt-8"
              >
                <Icon className="w-6 h-6 text-amber-400" />

                <div className="mt-6 font-display text-xl text-white">
                  {it.title}
                </div>

                <p className="mt-2 text-slate-400 text-sm leading-relaxed">
                  {it.body}
                </p>
              </div>
            );
          })}
        </div>

        {stats.length > 0 && (
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, idx) => (
              <div
                key={idx}
                className="
                  border
                  border-white/10
                  rounded-xl
                  p-6
                "
              >
                <div className="font-display text-4xl text-amber-400">
                  {s.value}
                </div>

                <div className="text-xs uppercase tracking-[0.24em] text-slate-400 mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ServicesSection() {
  const { data: services = [], isLoading } = useQuery({
    queryKey: ["home-services"],
    queryFn: () =>
      api
        .get("/services", {
          params: {
            is_active: true,
            limit: 6,
          },
        })
        .then((r) => r.data.items),
  });

  return (
    <section
      className="
        max-w-7xl
        mx-auto
        px-6
        lg:px-10
        py-24
        lg:py-28
      "
      data-testid="home-services-section"
    >
      <div
        className="
          flex
          flex-col
          md:flex-row
          md:items-end
          justify-between
          gap-6
          mb-10
          lg:mb-12
        "
      >
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
            What we offer
          </div>

          <h2
            className="
              font-display
              text-4xl
              sm:text-5xl
              text-slate-950
              leading-tight
            "
          >
            Travel services, thoughtfully arranged.
          </h2>
        </div>

        <Link
          to="/services"
          className="
            text-sm
            font-medium
            text-slate-700
            hover:text-amber-600
            transition-colors
            flex
            items-center
            gap-1
          "
          data-testid="home-services-view-all"
        >
          View all services
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400">
          Loading services…
        </div>
      ) : services.length === 0 ? (
        <div className="py-12 text-center text-slate-400">
          No services published yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.slice(0, 6).map((svc) => {
            const Icon = iconMap[svc.icon] || Sparkles;

            return (
              <Link
                key={svc.id}
                to="/services"
                className="
                  group
                  overflow-hidden
                  rounded-2xl
                  bg-white
                  border
                  border-slate-200
                  hover:-translate-y-1
                  hover:shadow-xl
                  transition-all
                  duration-500
                "
                data-testid={`home-service-${svc.slug}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={svc.image_url}
                    alt={svc.title}
                    loading="lazy"
                    className="
                      w-full
                      h-full
                      object-cover
                      group-hover:scale-105
                      transition-transform
                      duration-700
                    "
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  <div
                    className="
                      absolute
                      top-4
                      left-4
                      w-10
                      h-10
                      rounded-full
                      bg-white/95
                      backdrop-blur
                      grid
                      place-items-center
                      shadow-md
                    "
                  >
                    <Icon className="w-5 h-5 text-amber-600" />
                  </div>
                </div>

                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-amber-600">
                    {svc.tagline}
                  </div>

                  <h3
                    className="
                      mt-2
                      font-display
                      text-xl
                      lg:text-2xl
                      text-slate-950
                      group-hover:text-amber-700
                      transition-colors
                    "
                  >
                    {svc.title}
                  </h3>

                  <p
                    className="
                      mt-2
                      text-sm
                      text-slate-600
                      leading-relaxed
                      line-clamp-2
                    "
                  >
                    {svc.body}
                  </p>

                  <div
                    className="
                      mt-4
                      text-xs
                      uppercase
                      tracking-[0.2em]
                      text-slate-400
                      flex
                      items-center
                      gap-1
                    "
                  >
                    Explore
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FeaturedPackages() {
  const { data: packages = [] } = useQuery({
    queryKey: ["home-packages"],
    queryFn: () =>
      api
        .get("/packages", {
          params: {
            is_active: true,
            limit: 6,
          },
        })
        .then((r) => r.data.items),
  });

  const featured = packages
    .filter((p) => p.featured)
    .slice(0, 3);

  const list = featured.length
    ? featured
    : packages.slice(0, 3);

  return (
    <section
      className="bg-white"
      data-testid="featured-packages"
    >
      <div
        className="
          max-w-7xl
          mx-auto
          px-6
          lg:px-10
          py-28
        "
      >
        <div
          className="
            flex
            flex-col
            md:flex-row
            md:items-end
            justify-between
            gap-6
            mb-12
          "
        >
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
              Signature journeys
            </div>

            <h2
              className="
                font-display
                text-4xl
                sm:text-5xl
                text-slate-950
                leading-tight
              "
            >
              Featured tour packages.
            </h2>
          </div>

          <Link
            to="/packages"
            className="
              text-sm
              text-slate-700
              hover:text-slate-950
              flex
              items-center
              gap-1
            "
            data-testid="view-all-packages"
          >
            See all packages
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {list.map((p) => (
            <Link
              to={`/packages/${p.slug}`}
              key={p.id}
              className="
                group
                rounded-2xl
                overflow-hidden
                border
                border-slate-200
                bg-white
                hover:-translate-y-1
                transition-transform
              "
              data-testid={`pkg-card-${p.slug}`}
            >
              <div
                className="
                  relative
                  aspect-[4/3]
                  overflow-hidden
                "
              >
                <img
                  src={p.banner_url}
                  alt={p.name}
                  className="
                    w-full
                    h-full
                    object-cover
                    group-hover:scale-105
                    transition-transform
                    duration-700
                  "
                />

                <div
                  className="
                    absolute
                    top-3
                    left-3
                    bg-white/90
                    backdrop-blur
                    px-3
                    py-1
                    rounded-full
                    text-xs
                    font-medium
                  "
                >
                  {p.duration_days}D · {p.duration_nights}N
                </div>
              </div>

              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-600">
                  {p.destination}
                </div>

                <div
                  className="
                    mt-2
                    font-display
                    text-2xl
                    text-slate-950
                    group-hover:text-amber-700
                    transition-colors
                  "
                >
                  {p.name}
                </div>

                <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                  {p.overview}
                </p>

                <div className="mt-5 flex items-center justify-between">
                  <div className="text-sm text-slate-500">
                    From{" "}
                    <span className="text-slate-950 font-medium">
                      ₹
                      {p.price_from?.toLocaleString?.("en-IN")}
                    </span>
                  </div>

                  <span
                    className="
                      text-sm
                      text-slate-700
                      group-hover:text-amber-700
                      flex
                      items-center
                      gap-1
                    "
                  >
                    Explore
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsRow() {
  const { data: items = [] } = useQuery({
    queryKey: ["home-destinations"],
    queryFn: () =>
      api
        .get("/destinations", {
          params: {
            is_active: true,
            limit: 6,
          },
        })
        .then((r) => r.data.items),
  });

  const { data: packages = [] } = useQuery({
    queryKey: ["home-destinations-pkgs"],
    queryFn: () =>
      api
        .get("/packages", {
          params: {
            is_active: true,
            limit: 60,
          },
        })
        .then((r) => r.data.items),
  });

  const priceByDest = {};

  packages.forEach((p) => {
    if (!p.destination || !p.price_from) return;

    const key = p.destination.toLowerCase();

    if (
      !priceByDest[key] ||
      p.price_from < priceByDest[key]
    ) {
      priceByDest[key] = p.price_from;
    }
  });

  return (
    <section
      className="
        max-w-7xl
        mx-auto
        px-6
        lg:px-10
        py-28
      "
      data-testid="destinations-section"
    >
      <div
        className="
          flex
          flex-col
          md:flex-row
          md:items-end
          justify-between
          gap-6
          mb-12
        "
      >
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
            Places we love
          </div>

          <h2
            className="
              font-display
              text-4xl
              sm:text-5xl
              text-slate-950
              leading-tight
            "
          >
            Popular Destinations.
          </h2>
        </div>

        <Link
          to="/destinations"
          className="
            text-sm
            text-slate-700
            hover:text-slate-950
            flex
            items-center
            gap-1
          "
        >
          View All
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div
        className="
          flex
          md:grid
          md:grid-cols-3
          lg:grid-cols-6
          gap-4
          overflow-x-auto
          md:overflow-visible
          snap-x
          snap-mandatory
          scrollbar-hide
          pb-2
        "
      >
        {items.map((d) => {
          const price =
            priceByDest[(d.name || "").toLowerCase()];

          return (
            <Link
              to="/destinations"
              key={d.id}
              className="
                group
                relative
                aspect-[3/4]
                rounded-xl
                overflow-hidden
                min-w-[78vw]
                sm:min-w-[55vw]
                md:min-w-0
                snap-start
              "
              data-testid={`dest-${d.slug}`}
            >
              <img
                src={d.image_url}
                alt={d.name}
                className="
                  absolute
                  inset-0
                  w-full
                  h-full
                  object-cover
                  group-hover:scale-110
                  transition-transform
                  duration-700
                "
              />

              <div
                className="
                  absolute
                  inset-0
                  bg-gradient-to-b
                  from-transparent
                  via-transparent
                  to-black/75
                "
              />

              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="text-white font-display text-lg leading-tight">
                  {d.name}
                </div>

                {price ? (
                  <div className="text-amber-300 text-xs mt-1 font-medium">
                    From ₹{price.toLocaleString("en-IN")}
                  </div>
                ) : (
                  <div className="text-white/70 text-xs mt-1">
                    {d.region}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function PopularTrips() {
  const { data: trips = [], isLoading } = useQuery({
    queryKey: ["home-popular-trips"],
    queryFn: () =>
      api
        .get("/destinations", {
          params: {
            is_active: true,
            limit: 12,
          },
        })
        .then((r) => r.data.items),
  });

  const visibleTrips = trips.slice(0, 12);

  const [currentTrip, setCurrentTrip] = useState(0);

  const sliderRef = useRef(null);

  const handleTripScroll = () => {
    const slider = sliderRef.current;

    if (!slider || !visibleTrips.length) return;

    const cards = slider.children;

    if (!cards.length) return;

    const scrollLeft = slider.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;

    Array.from(cards).forEach((card, index) => {
      const distance = Math.abs(
        card.offsetLeft - scrollLeft - 16
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setCurrentTrip(closestIndex);
  };

  const goToTrip = (index) => {
    const slider = sliderRef.current;

    if (!slider || !visibleTrips.length) return;

    const safeIndex = Math.max(
      0,
      Math.min(index, visibleTrips.length - 1)
    );

    const card = slider.children[safeIndex];

    if (card) {
      slider.scrollTo({
        left: card.offsetLeft - 16,
        behavior: "smooth",
      });
    }

    setCurrentTrip(safeIndex);
  };

  return (
    <section
      className="
        bg-brand-navy-900
        text-white
        py-20
        sm:py-24
      "
      data-testid="popular-trips-section"
    >
      <div
        className="
          max-w-7xl
          mx-auto
          px-4
          sm:px-6
          lg:px-10
        "
      >
        <div
          className="
            flex
            flex-col
            md:flex-row
            md:items-end
            justify-between
            gap-5
            mb-10
          "
        >
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-amber-400 mb-3">
              Popular trips
            </div>

            <h2
              className="
                font-display
                text-3xl
                sm:text-4xl
                lg:text-5xl
                leading-tight
              "
            >
              Explore one journey at a time.
            </h2>

            <p className="mt-3 text-slate-300 text-sm sm:text-base max-w-xl">
              Discover our popular travel routes and book your next journey
              with comfortable transportation and reliable service.
            </p>
          </div>

          <Link
            to="/destinations"
            className="
              text-sm
              text-white/80
              hover:text-white
              flex
              items-center
              gap-1
              shrink-0
            "
          >
            View all trips
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-slate-400">
            Loading trips…
          </div>
        ) : visibleTrips.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            No trips available.
          </div>
        ) : (
          <div
            ref={sliderRef}
            onScroll={handleTripScroll}
            className="
              flex
              gap-5
              overflow-x-auto
              snap-x
              snap-mandatory
              pb-5
              -mx-4
              px-4
              md:grid
              md:grid-cols-3
              md:overflow-visible
              md:mx-0
              md:px-0
              scrollbar-hide
            "
            style={{ scrollBehavior: "smooth" }}
          >
            {visibleTrips.map((trip, index) => (
              <article
                key={trip.id}
                className="
                  w-[88%]
                  sm:w-[70%]
                  md:w-auto
                  shrink-0
                  snap-start
                  overflow-hidden
                  rounded-2xl
                  bg-white
                  text-slate-950
                  border
                  border-white/10
                  shadow-lg
                "
                data-testid={`popular-trip-${trip.slug}`}
              >
                <div
                  className="
                    relative
                    aspect-[16/10]
                    overflow-hidden
                  "
                >
                  <img
                    src={trip.image_url}
                    alt={trip.name}
                    loading="lazy"
                    className="
                      w-full
                      h-full
                      object-cover
                    "
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />

                  <div
                    className="
                      absolute
                      bottom-4
                      left-4
                      right-4
                      flex
                      items-end
                      justify-between
                      gap-3
                    "
                  >
                    <div>
                      <div className="text-white text-xs uppercase tracking-[0.22em]">
                        Popular trip
                      </div>

                      <h3 className="mt-1 font-display text-2xl sm:text-3xl text-white">
                        {trip.name}
                      </h3>
                    </div>

                    <div
                      className="
                        md:hidden
                        shrink-0
                        rounded-full
                        bg-black/45
                        backdrop-blur-sm
                        border
                        border-white/20
                        px-3
                        py-1.5
                        text-white
                        text-xs
                        font-medium
                        tracking-widest
                      "
                      aria-label={`Trip ${index + 1} of ${visibleTrips.length}`}
                    >
                      {String(index + 1).padStart(2, "0")} /{" "}
                      {String(visibleTrips.length).padStart(2, "0")}
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-6">
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {trip.desc ||
                      `Explore ${trip.name} comfortably with reliable transportation and professional travel services.`}
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <a
                      href="tel:+919866933180"
                      className="
                        flex
                        items-center
                        justify-center
                        gap-2
                        rounded-full
                        border
                        border-slate-200
                        px-4
                        py-3
                        text-sm
                        font-semibold
                        text-slate-900
                        hover:bg-slate-50
                        transition-colors
                      "
                      data-testid={`popular-trip-call-${trip.slug}`}
                    >
                      <Phone className="w-4 h-4" />
                      Call Now
                    </a>

                    <InquiryDialog
                      defaultMessage={`Hello! I'd like to book a trip to ${trip.name}. Please share the available options.`}
                      sourceRef={trip.slug}
                      source="home_popular_trips"
                      triggerTestId={`popular-trip-book-${trip.slug}`}
                    >
                      <Button
                        className="
                          w-full
                          rounded-full
                          bg-brand-navy-900
                          hover:bg-brand-navy-800
                          text-white
                          text-sm
                          font-semibold
                        "
                        data-testid={`popular-trip-book-${trip.slug}`}
                      >
                        Book Now
                        <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </InquiryDialog>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {visibleTrips.length > 1 && (
          <div
            className="
              mt-5
              flex
              items-center
              justify-center
              gap-3
              md:hidden
            "
          >
            <button
              type="button"
              onClick={() => goToTrip(currentTrip - 1)}
              disabled={currentTrip === 0}
              className="
                w-9
                h-9
                rounded-full
                border
                border-white/20
                grid
                place-items-center
                text-white
                disabled:opacity-30
                disabled:cursor-not-allowed
                hover:bg-white/10
                transition-colors
              "
              aria-label="Previous trip"
              data-testid="popular-trips-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div
              className="
                min-w-[58px]
                text-center
                text-xs
                text-white/80
                tracking-widest
              "
              data-testid="popular-trips-counter"
            >
              {String(currentTrip + 1).padStart(2, "0")} /{" "}
              {String(visibleTrips.length).padStart(2, "0")}
            </div>

            <button
              type="button"
              onClick={() => goToTrip(currentTrip + 1)}
              disabled={
                currentTrip === visibleTrips.length - 1
              }
              className="
                w-9
                h-9
                rounded-full
                border
                border-white/20
                grid
                place-items-center
                text-white
                disabled:opacity-30
                disabled:cursor-not-allowed
                hover:bg-white/10
                transition-colors
              "
              aria-label="Next trip"
              data-testid="popular-trips-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialsBlock() {
  const { data: items = [] } = useQuery({
    queryKey: ["testimonials"],
    queryFn: () =>
      api
        .get("/testimonials", {
          params: {
            is_active: true,
          },
        })
        .then((r) => r.data.items),
  });

  return (
    <section
      className="bg-slate-100"
      data-testid="testimonials-section"
    >
      <div
        className="
          max-w-7xl
          mx-auto
          px-6
          lg:px-10
          py-28
        "
      >
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
          Guest stories
        </div>

        <h2
          className="
            font-display
            text-4xl
            sm:text-5xl
            text-slate-950
            leading-tight
          "
        >
          Kind words from our travellers.
        </h2>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.slice(0, 4).map((t) => (
            <div
              key={t.id}
              className="
                bg-white
                rounded-2xl
                border
                border-slate-200
                p-6
              "
            >
              <div className="flex items-center gap-1 text-amber-500">
                {Array.from({
                  length: t.rating || 5,
                }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-amber-500"
                  />
                ))}
              </div>

              <p className="mt-5 text-slate-700 leading-relaxed">
                “{t.message}”
              </p>

              <div
                className="
                  mt-6
                  border-t
                  border-slate-200
                  pt-4
                  flex
                  items-center
                  gap-3
                "
              >
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt={t.name}
                    className="
                      w-10
                      h-10
                      rounded-full
                      object-cover
                    "
                  />
                ) : (
                  <div
                    className="
                      w-10
                      h-10
                      rounded-full
                      bg-slate-200
                      grid
                      place-items-center
                      font-display
                      text-slate-700
                    "
                  >
                    {t.name?.[0]}
                  </div>
                )}

                <div>
                  <div className="text-slate-950 font-medium text-sm">
                    {t.name}
                  </div>

                  <div className="text-slate-500 text-xs">
                    {t.location}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryPreview() {
  const { data: items = [] } = useQuery({
    queryKey: ["gallery-home"],
    queryFn: () =>
      api
        .get("/gallery", {
          params: {
            is_active: true,
            limit: 8,
          },
        })
        .then((r) => r.data.items),
  });

  return (
    <section
      className="
        max-w-7xl
        mx-auto
        px-6
        lg:px-10
        py-28
      "
      data-testid="gallery-preview"
    >
      <div
        className="
          flex
          justify-between
          items-end
          mb-12
        "
      >
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
            Frames of travel
          </div>

          <h2
            className="
              font-display
              text-4xl
              sm:text-5xl
              text-slate-950
              leading-tight
            "
          >
            A visual journal.
          </h2>
        </div>

        <Link
          to="/gallery"
          className="
            text-sm
            text-slate-700
            hover:text-slate-950
            flex
            items-center
            gap-1
          "
        >
          View gallery
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.slice(0, 8).map((g, i) => (
          <div
            key={g.id}
            className={`
              relative
              overflow-hidden
              rounded-xl
              ${
                i % 5 === 0
                  ? "md:row-span-2 aspect-[3/4]"
                  : "aspect-square"
              }
            `}
          >
            <img
              src={g.image_url}
              alt={g.title}
              className="
                w-full
                h-full
                object-cover
                hover:scale-105
                transition-transform
                duration-700
              "
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function FaqsPreview() {
  const { data: items = [] } = useQuery({
    queryKey: ["faqs-home"],
    queryFn: () =>
      api
        .get("/faqs", {
          params: {
            is_active: true,
            limit: 5,
          },
        })
        .then((r) => r.data.items),
  });

  return (
    <section
      className="
        max-w-4xl
        mx-auto
        px-6
        lg:px-10
        py-28
      "
      data-testid="faqs-preview"
    >
      <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">
        Frequently asked
      </div>

      <h2
        className="
          font-display
          text-4xl
          sm:text-5xl
          text-slate-950
          leading-tight
        "
      >
        Good questions,
        <br />
        candid answers.
      </h2>

      <div
        className="
          mt-10
          divide-y
          divide-slate-200
          border-t
          border-slate-200
        "
      >
        {items.map((f) => (
          <details
            key={f.id}
            className="py-6 group"
            data-testid={`faq-${f.id}`}
          >
            <summary
              className="
                cursor-pointer
                flex
                items-center
                justify-between
                gap-6
                list-none
              "
            >
              <span className="font-display text-xl text-slate-950">
                {f.question}
              </span>

              <span
                className="
                  text-amber-600
                  group-open:rotate-45
                  transition-transform
                "
              >
                ＋
              </span>
            </summary>

            <p className="mt-3 text-slate-600 leading-relaxed">
              {f.answer}
            </p>
          </details>
        ))}
      </div>

      <div className="mt-10">
        <Link to="/faqs">
          <Button
            variant="outline"
            className="rounded-full"
            data-testid="all-faqs-btn"
          >
            All FAQs →
          </Button>
        </Link>
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section
      className="
        max-w-7xl
        mx-auto
        px-6
        lg:px-10
        pb-28
      "
    >
      <div
        className="
          relative
          rounded-3xl
          overflow-hidden
          bg-brand-navy-900
          text-white
          p-10
          md:p-16
        "
      >
        <div className="grain" />

        <div
          className="
            grid
            md:grid-cols-2
            gap-10
            items-center
            relative
          "
        >
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-4">
              Let&apos;s begin
            </div>

            <h2
              className="
                font-display
                text-4xl
                md:text-5xl
                leading-tight
              "
            >
              Ready to design your journey?
            </h2>

            <p className="mt-4 text-slate-300 max-w-md">
              Share your dates and dreams — a curator will craft a proposal
              within 24 hours.
            </p>
          </div>

          <div className="flex md:justify-end">
            <CTAButtons
              label="a custom journey"
              sourceRef="home-cta"
              dark
              testIdPrefix="home"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/*
|--------------------------------------------------------------------------
| HOW BOOKING WORKS
|--------------------------------------------------------------------------
| Desktop:
|   3 horizontal cards
|
| Mobile:
|   Compact vertical flow
|   Prevents the section from becoming too tall/overloaded.
|--------------------------------------------------------------------------
*/

function HowBookingWorks() {
  const steps = [
    {
      number: "01",
      title: "Choose Your Trip",
      description:
        "Select your destination or travel requirement.",
      icon: Route,
    },
    {
      number: "02",
      title: "Choose Your Vehicle",
      description:
        "Select the vehicle according to your group size.",
      icon: Sparkles,
    },
    {
      number: "03",
      title: "Confirm Your Booking",
      description:
        "Submit your details and confirm your ride.",
      icon: ShieldCheck,
    },
  ];

  return (
    <section
      className="
        bg-slate-50
        py-16
        sm:py-20
        lg:py-28
      "
      data-testid="how-booking-works-section"
    >
      <div
        className="
          max-w-7xl
          mx-auto
          px-5
          sm:px-6
          lg:px-10
        "
      >
        {/* Heading */}
        <div
          className="
            text-center
            max-w-3xl
            mx-auto
          "
        >
          <div
            className="
              text-[10px]
              sm:text-xs
              uppercase
              tracking-[0.28em]
              text-amber-600
              mb-3
              sm:mb-4
            "
          >
            How it works
          </div>

          <h2
            className="
              font-display
              text-3xl
              sm:text-5xl
              lg:text-6xl
              text-slate-950
              leading-[1.05]
            "
          >
            <span className="sm:hidden">
              Book Your Ride
            </span>

            <span className="hidden sm:inline">
              Book Your Ride In 3 Simple Steps
            </span>
          </h2>
        </div>

        {/* =========================
            MOBILE FLOW
            ========================= */}
        <div
          className="
            mt-8
            sm:hidden
            relative
            max-w-md
            mx-auto
          "
        >
          {/* Vertical connecting line */}
          <div
            className="
              absolute
              left-[19px]
              top-7
              bottom-7
              w-px
              bg-slate-200
            "
          />

          <div className="relative space-y-5">
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.number}
                  className="
                    relative
                    flex
                    items-start
                    gap-4
                  "
                  data-testid={`booking-step-mobile-${step.number}`}
                >
                  {/* Number */}
                  <div
                    className="
                      relative
                      z-10
                      shrink-0
                      w-10
                      h-10
                      rounded-full
                      bg-brand-navy-900
                      text-white
                      flex
                      items-center
                      justify-center
                      text-xs
                      font-semibold
                      shadow-sm
                    "
                  >
                    {step.number}
                  </div>

                  {/* Content */}
                  <div
                    className="
                      flex-1
                      min-w-0
                      bg-white
                      border
                      border-slate-200
                      rounded-xl
                      px-4
                      py-4
                    "
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="
                          shrink-0
                          w-9
                          h-9
                          rounded-full
                          bg-slate-950
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <Icon className="w-4 h-4 text-white" />
                      </div>

                      <div className="min-w-0">
                        <h3
                          className="
                            font-display
                            text-lg
                            text-slate-950
                            leading-tight
                          "
                        >
                          {step.title}
                        </h3>

                        <p
                          className="
                            mt-1.5
                            text-xs
                            text-slate-600
                            leading-relaxed
                          "
                        >
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* =========================
            DESKTOP FLOW
            ========================= */}
        <div
          className="
            hidden
            sm:grid
            mt-12
            lg:mt-14
            grid-cols-3
            gap-5
            lg:gap-6
          "
        >
          {steps.map((step) => {
            const Icon = step.icon;

            return (
              <div
                key={step.number}
                className="
                  group
                  bg-white
                  border
                  border-slate-200
                  rounded-2xl
                  p-6
                  lg:p-8
                  shadow-sm
                  hover:shadow-lg
                  transition-shadow
                  duration-300
                "
                data-testid={`booking-step-${step.number}`}
              >
                <div className="flex items-center gap-3 mb-7">
                  <div
                    className="
                      w-10
                      h-10
                      shrink-0
                      rounded-full
                      bg-brand-navy-900
                      text-white
                      flex
                      items-center
                      justify-center
                      text-xs
                      sm:text-sm
                      font-semibold
                      tracking-wide
                    "
                  >
                    {step.number}
                  </div>

                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                <div
                  className="
                    w-12
                    h-12
                    rounded-full
                    bg-brand-navy-900
                    flex
                    items-center
                    justify-center
                    mb-6
                  "
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>

                <h3
                  className="
                    font-display
                    text-2xl
                    sm:text-[26px]
                    text-slate-950
                    leading-tight
                  "
                >
                  {step.title}
                </h3>

                <p
                  className="
                    mt-3
                    text-sm
                    sm:text-base
                    text-slate-600
                    leading-7
                    max-w-sm
                  "
                >
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Book Now */}
        <div
          className="
            mt-8
            sm:mt-10
            lg:mt-12
            flex
            justify-center
          "
        >
          <InquiryDialog triggerTestId="how-booking-works-book-btn">
            <Button
              className="
                rounded-full
                bg-[#b9923b]
                hover:bg-[#a47f2f]
                text-slate-950
                px-7
                sm:px-8
                h-11
                sm:h-12
                text-sm
                font-semibold
                shadow-md
              "
              data-testid="how-booking-works-book-btn"
            >
              Book Now
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </InquiryDialog>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: homepage } = useQuery({
    queryKey: ["homepage"],
    queryFn: () =>
      api.get("/homepage").then((r) => r.data),
  });

  return (
    <>
      <Hero />

      <section
        className="
          max-w-7xl
          mx-auto
          px-4
          sm:px-6
          lg:px-10
          mt-0
          relative
          z-20
        "
        data-testid="booking-search-section"
      >
        <BookingSearch />
      </section>

      <Benefits />

      <DestinationsRow />

      <PopularTrips />

      <ChooseYourVehicle />

      <ServicesSection />

      <WhyChoose
        items={homepage?.why_choose_us || []}
        stats={homepage?.stats || []}
      />

      <FeaturedPackages />

      <HowBookingWorks />

      <AboutBlock about={homepage?.about} />

      <GalleryPreview />

      <TestimonialsBlock />

      <FaqsPreview />

      <ContactCTA />
    </>
  );
}